import api from './api';

/**
 * Contacts API Service
 * All endpoints require admin authentication
 */

/**
 * Get all contacts with optional filters
 * @param {Object} filters - Filter parameters
 * @param {string} filters.type - Filter by contact type (contact_form, quick_signup)
 * @param {string} filters.user_type - Filter by user type (solo_developer, entrepreneur, startup_team)
 * @param {string} filters.start_date - Filter by start date (YYYY-MM-DD)
 * @param {string} filters.end_date - Filter by end date (YYYY-MM-DD)
 * @param {string} filters.search - Search by name or email
 * @param {number} filters.page - Page number for pagination
 * @param {number} filters.per_page - Items per page
 * @returns {Promise} API response with contacts list
 */
export const getContacts = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type);
  if (filters.user_type) params.append('user_type', filters.user_type);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (filters.per_page) params.append('per_page', filters.per_page);

  const response = await api.get(`/api/contacts?${params.toString()}`);
  return response.data;
};

/**
 * Export contacts to CSV with optional filters
 * @param {Object} filters - Filter parameters (same as getContacts)
 * @returns {Promise} Blob with CSV file
 */
export const exportContacts = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type);
  if (filters.user_type) params.append('user_type', filters.user_type);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.search) params.append('search', filters.search);

  const response = await api.get(`/api/contacts/export?${params.toString()}`, {
    responseType: 'blob'
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `contacts_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return response;
};

/**
 * Submit contact form (public endpoint, no auth required)
 * @param {Object} data - Contact form data
 * @returns {Promise} API response
 */
export const submitContact = async (data) => {
  const response = await api.post('/contact', data);
  return response.data;
};

/**
 * Update user type for a contact
 * @param {string} email - Contact email
 * @param {string} userType - New user type
 * @returns {Promise} API response
 */
export const updateContactUserType = async (email, userType) => {
  const response = await api.put('/contact/user-type', { email, user_type: userType });
  return response.data;
};
